import { createClient } from '@/lib/supabase/client';
export type S8StockHead =
  | { state: 'PRIOR_INTENT_PENDING'; medicationId: string; clientId: string; operationId: string }
  | { state: 'INITIALIZATION_REQUIRED'; medicationId: string; clientId: string }
  | {
      state: 'INITIALIZED';
      medicationId: string;
      clientId: string;
      balance: string;
      version: string;
    };
export async function readS8StockHead(payload: Record<string, unknown>): Promise<S8StockHead> {
  const { data, error } = await createClient().rpc('read_s8_stock_head_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (
    !data ||
    data.medicationId !== payload.medication_id ||
    data.clientId !== payload.client_id ||
    !['INITIALIZED', 'INITIALIZATION_REQUIRED', 'PRIOR_INTENT_PENDING'].includes(data.state) ||
    (data.state === 'PRIOR_INTENT_PENDING' && !/^[0-9a-f-]{36}$/i.test(data.operationId)) ||
    (data.state === 'INITIALIZED' &&
      (!/^\d+(\.\d{1,2})?$/.test(data.balance) || !/^\d+$/.test(data.version)))
  )
    throw new Error('The stock balance could not be verified. Refresh and review it again.');
  return data as S8StockHead;
}
export async function initializeS8Stock(payload: Record<string, unknown>) {
  await prepareS8Stock({ ...payload, command_kind: 'BASELINE' });
  const { data, error } = await createClient().rpc('initialize_s8_stock_head_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (
    !data ||
    data.medication_id !== payload.medication_id ||
    data.client_id !== payload.client_id ||
    typeof data.baseline_id !== 'string' ||
    data.version !== 0 ||
    !Number.isFinite(data.balance)
  )
    throw new Error('The baseline outcome could not be verified. Check its receipt.');
  return data;
}
export async function recoverS8Stock(
  payload: Record<string, unknown>,
): Promise<'QUARANTINED' | 'COMMITTED' | 'NOT_OBSERVED' | 'PREPARED' | 'RETIRED'> {
  const { data, error } = await createClient().rpc('recover_s8_stock_command_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (!['QUARANTINED', 'COMMITTED', 'NOT_OBSERVED', 'PREPARED', 'RETIRED'].includes(data?.state))
    throw new Error('The stock command outcome could not be verified.');
  return data.state;
}
export const S8_STOCK_RECOVERY_EVENT = 'statura:s8-stock-change';
function changed() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(S8_STOCK_RECOVERY_EVENT));
}
export async function prepareS8Stock(payload: Record<string, unknown>) {
  const { data, error } = await createClient().rpc('prepare_s8_stock_command_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (
    !['PREPARED', 'COMMITTED'].includes(data?.state) ||
    data?.operationId !== payload.operation_id
  )
    throw new Error('The stock command preparation could not be verified.');
  return data;
}
export async function retireS8Stock(operationId: string): Promise<'RETIRED' | 'COMMITTED'> {
  const { data, error } = await createClient().rpc('retire_s8_stock_command_controlled', {
    p_payload: { operation_id: operationId },
  });
  if (error) throw error;
  if (data?.state !== 'RETIRED' && data?.state !== 'COMMITTED')
    throw new Error('The stock command retirement could not be verified.');
  return data.state;
}
const PREFIX = 'statura:s8-stock:';
export function s8StockRecoveryScope(actor: string, tenant: string) {
  return `${PREFIX}${encodeURIComponent(actor)}:${encodeURIComponent(tenant)}`;
}
export function s8StockHandles(scope: string): string[] {
  try {
    const data: unknown = JSON.parse(sessionStorage.getItem(scope) ?? '[]');
    return Array.isArray(data)
      ? data.filter((key): key is string => typeof key === 'string' && /^[0-9a-f-]{36}$/i.test(key))
      : [];
  } catch {
    return [];
  }
}
export function rememberS8StockHandle(scope: string, key: string) {
  try {
    sessionStorage.setItem(scope, JSON.stringify([...new Set([...s8StockHandles(scope), key])]));
    changed();
    return true;
  } catch {
    return false;
  }
}
export function forgetS8StockHandle(scope: string, key: string) {
  try {
    sessionStorage.setItem(
      scope,
      JSON.stringify(s8StockHandles(scope).filter((item) => item !== key)),
    );
    changed();
  } catch {
    /* retain opaque handle */
  }
}
export function clearS8StockHandles() {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index--) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    /* no clinical payload is stored */
  }
}
export type S8StockHistoryEntry = {
  id: string;
  action: string;
  quantity: number;
  balance_before: number;
  balance_after: number;
  recorded_at: string;
  stock_version?: string;
};
export async function readS8StockHistory(
  payload: Record<string, unknown>,
): Promise<{ entries: S8StockHistoryEntry[]; nextCursor: string | null }> {
  const { data, error } = await createClient().rpc('read_s8_stock_history_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (
    data?.section !== payload.section ||
    !Array.isArray(data?.entries) ||
    data.entries.some(
      (entry: S8StockHistoryEntry) =>
        !entry ||
        typeof entry.id !== 'string' ||
        typeof entry.action !== 'string' ||
        !Number.isFinite(entry.quantity) ||
        !Number.isFinite(entry.balance_before) ||
        !Number.isFinite(entry.balance_after) ||
        !Number.isFinite(Date.parse(entry.recorded_at)),
    )
  )
    throw new Error('The stock history could not be verified.');
  return {
    entries: data.entries,
    nextCursor: typeof data.nextCursor === 'string' ? data.nextCursor : null,
  };
}
/** A deadline reports an unknown outcome; it never cancels or retires server work. */
export async function withinS8StockBudget<T>(
  work: PromiseLike<T>,
  milliseconds = 12_000,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve(work),
      new Promise<T>((_resolve, reject) => {
        timer = setTimeout(
          () =>
            reject(new Error('The stock operation outcome is not verified. Check its receipt.')),
          milliseconds,
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
export function s8StockRefusal(error: unknown): string | null {
  const message =
    error && typeof error === 'object' && 'message' in error ? String(error.message) : '';
  if (
    [
      'program_write_family_unavailable',
      'program_lens_not_authorised',
      'program_context_unavailable',
    ].some((token) => message.includes(token))
  )
    return 'A current residential workspace is unavailable. Retained stock needs authorized operational reconciliation; do not treat it as zero or repeat a physical movement.';
  if (message.includes('s8_medication_origin_reconciliation_required'))
    return 'This medication order needs an explicit residential origin reconciliation before stock can be changed.';
  if (message.includes('s8_medication_outside_residential_stock_scope'))
    return 'This medication order is outside residential stock scope.';
  if (message.includes('s8_stock_version_stale'))
    return 'The stock balance changed. Retire this request, then review physical stock and the current balance before preparing a new request.';
  if (message.includes('s8_stock_balance_out_of_range'))
    return 'The movement exceeds available stock or the supported stock range. Check the receipt and reconcile physical stock.';
  if (
    message.includes('hark_s8_one_prepared_per_actor_medication') ||
    message.includes('s8_prior_intent_unresolved')
  )
    return 'A prior request for this medication order is unresolved. Check its receipt before another entry.';
  return null;
}
export type S8StockOrder = {
  id: string;
  clientId: string;
  label: string;
  status: string;
  origin: 'RAC' | 'RECONCILIATION_REQUIRED';
};
export async function listS8StockOrders(
  payload: Record<string, unknown>,
): Promise<{ orders: S8StockOrder[]; nextCursor: string | null }> {
  const { data, error } = await createClient().rpc('list_s8_stock_orders_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (
    !Array.isArray(data?.orders) ||
    data.orders.some(
      (row: S8StockOrder) =>
        !row ||
        typeof row.id !== 'string' ||
        typeof row.clientId !== 'string' ||
        typeof row.label !== 'string' ||
        typeof row.status !== 'string' ||
        !['RAC', 'RECONCILIATION_REQUIRED'].includes(row.origin),
    )
  )
    throw new Error('Stock medication orders could not be verified.');
  return {
    orders: data.orders,
    nextCursor: typeof data.nextCursor === 'string' ? data.nextCursor : null,
  };
}

export type S8AdministrationChoice = { id: string; administeredAt: string; dose: string };
export async function listS8StockAdministrations(payload: Record<string, unknown>): Promise<{
  administrations: S8AdministrationChoice[];
  timezone: string;
  nextCursor: string | null;
}> {
  const { data, error } = await createClient().rpc('list_s8_stock_administrations_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (
    !Array.isArray(data?.administrations) ||
    typeof data.timezone !== 'string' ||
    data.administrations.some(
      (row: S8AdministrationChoice) =>
        !row ||
        typeof row.id !== 'string' ||
        typeof row.dose !== 'string' ||
        !Number.isFinite(Date.parse(row.administeredAt)),
    )
  )
    throw new Error('Recent administrations could not be verified.');
  return {
    administrations: data.administrations,
    timezone: data.timezone,
    nextCursor: typeof data.nextCursor === 'string' ? data.nextCursor : null,
  };
}
export type S8QuarantineSummary = {
  operationId: string;
  administeredAt: string;
  quantity: string | null;
  state: 'QUARANTINED' | 'SUPERSEDED';
  incidentPending: boolean;
};
export async function listS8StockQuarantines(payload: Record<string, unknown>): Promise<{
  assertions: S8QuarantineSummary[];
  pendingIncidents: { operationId: string; kind: string; receivedAt: string }[];
  nextCursor: string | null;
}> {
  const { data, error } = await createClient().rpc('list_s8_stock_quarantines_controlled', {
    p_payload: payload,
  });
  if (error) throw error;
  if (
    !Array.isArray(data?.assertions) ||
    !Array.isArray(data?.pendingIncidents) ||
    data.pendingIncidents.some(
      (row: { operationId: string; kind: string; receivedAt: string }) =>
        !row ||
        typeof row.operationId !== 'string' ||
        !['QUARANTINE', 'BREAK_GLASS'].includes(row.kind) ||
        !Number.isFinite(Date.parse(row.receivedAt)),
    ) ||
    data.assertions.some(
      (row: S8QuarantineSummary) =>
        !row ||
        typeof row.operationId !== 'string' ||
        !Number.isFinite(Date.parse(row.administeredAt)) ||
        !['QUARANTINED', 'SUPERSEDED'].includes(row.state) ||
        typeof row.incidentPending !== 'boolean',
    )
  )
    throw new Error('Retained dose assertions could not be verified.');
  return {
    assertions: data.assertions,
    pendingIncidents: data.pendingIncidents,
    nextCursor: typeof data.nextCursor === 'string' ? data.nextCursor : null,
  };
}

export async function retryS8PendingIncident(
  quarantineId: string,
  kind: 'QUARANTINE' | 'BREAK_GLASS' = 'QUARANTINE',
): Promise<{ incidentPending: boolean }> {
  const { data, error } =
    kind === 'BREAK_GLASS'
      ? await createClient().rpc('retry_s8_break_glass_incident_controlled', {
          p_operation_id: quarantineId,
        })
      : await createClient().rpc('retry_s8_pending_incident_controlled', {
          quarantine_id: quarantineId,
        });
  if (error) throw error;
  if (
    typeof data?.incidentPending !== 'boolean' ||
    (!data.incidentPending && typeof data.incidentId !== 'string')
  )
    throw new Error('Incident capture could not be verified. Check retained assertions.');
  return { incidentPending: data.incidentPending };
}

export function isS8StockStale(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'message' in error &&
    String(error.message).includes('s8_stock_version_stale')
  );
}
